import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { formatMinor } from "@shared/money.ts";
import { dateCertaintyLabel } from "@shared/upcoming.ts";
import { amountsToSentence, parseHouseholdView, tripMoneyView, type HouseholdView } from "@shared/money-view.ts";
import { formatDateLabel } from "@shared/time.ts";
import { knownCostTotals } from "@shared/ledger.ts";
import { useSession } from "../../app/session.tsx";
import { useTrip } from "../../app/trip.tsx";
import { fetchLive } from "../../lib/api.ts";
import { fxRate, type LivePayload } from "../../lib/live.ts";
import { readHouseholdView, writeHouseholdView } from "../../lib/household-view.ts";
import { PaymentForm, SettlementForm } from "./PaymentForms.tsx";

export function MoneyHomePage() {
  const { trip, refresh, loading } = useTrip();
  const { role } = useSession();
  const [params, setParams] = useSearchParams();
  const [live, setLive] = useState<LivePayload | null>(null);
  const [listFilter, setListFilter] = useState<"unpaid" | "paid" | "all">("unpaid");
  const [sheet, setSheet] = useState<"pay" | "settle" | null>(
    params.get("pay") ? "pay" : params.get("settle") ? "settle" : null,
  );

  const view = parseHouseholdView(params.get("view") ?? readHouseholdView(), trip?.households ?? []);

  useEffect(() => {
    void fetchLive().then((raw) => setLive(raw as LivePayload)).catch(() => setLive(null));
  }, []);

  function setView(next: HouseholdView) {
    writeHouseholdView(next);
    const nextParams = new URLSearchParams(params);
    if (next === "all") nextParams.delete("view");
    else nextParams.set("view", next);
    setParams(nextParams, { replace: true });
  }

  if (!trip && loading) {
    return (
      <main className="page money-page">
        <div className="skeleton" style={{ height: 36, width: "40%", marginBottom: 16 }} />
        <div className="skeleton" style={{ height: 120 }} />
      </main>
    );
  }
  if (!trip) return <main className="page">Money needs the trip document.</main>;

  const money = tripMoneyView(trip, view);
  const { summary, schedule, bookingRows, ledger } = money;
  const totals = knownCostTotals(trip);
  const rate = fxRate(live?.fx);
  const viewer = view === "all" ? "Everyone" : summary.viewLabel;
  const counterpart = view === "all"
    ? null
    : trip.households.find((h) => h.id !== view)?.label ?? "the other household";
  const rows = bookingRows.filter((row) => {
    if (listFilter === "all") return true;
    if (listFilter === "paid") return row.paid || row.paymentState === "paid";
    return !row.paid && row.paymentState !== "paid";
  });

  return (
    <main className="page money-page">
      <header className="money-header">
        <div>
          <p className="kicker">Family statement</p>
          <h1 className="page-title">Money</h1>
        </div>
        <div className="view-switch" role="radiogroup" aria-label="Viewing household">
          <p className="view-label" id="viewing-label">Viewing</p>
          {[{ id: "all" as const, label: "Everyone" }, ...trip.households.map((h) => ({ id: h.id, label: h.label }))].map((option) => (
            <button
              key={option.id}
              type="button"
              role="radio"
              aria-checked={view === option.id}
              className={`chip ${view === option.id ? "selected" : ""}`}
              onClick={() => setView(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </header>
      <p className="muted">Viewing {viewer}. Everyone can still read every amount.</p>

      <section className="money-summary" aria-labelledby="cover-heading">
        <h2 id="cover-heading" className="section-title">
          {view === "all" ? "Still payable to suppliers" : `${viewer} still needs to cover`}
        </h2>
        <div className="money-figures">
          {summary.remainingSupplier.length === 0 ? (
            <p className="money-figure">Nothing left for suppliers</p>
          ) : summary.remainingSupplier.map((line) => (
            <p key={line.currency} className="money-figure">
              <span className="amount-pair tabular">{formatMinor(line.currency, line.amountMinor)}</span>
            </p>
          ))}
        </div>
        <p>
          Unpaid booking share: {amountsToSentence(summary.remainingSupplier, "nothing")}.
          {view === "all"
            ? " Household reimbursements are listed separately below."
            : ` Reimbursement to ${summary.reimbursementToLabel ?? "the other household"}: ${amountsToSentence(summary.reimbursementOwed, "nothing")}.`}
        </p>
        <p className="muted">
          Pounds and baht are never added together.
        </p>
        <details className="help-fold">
          <summary>About these numbers</summary>
          <p>Choosing Chris or Paul only changes whose figures are highlighted. Everyone can still read every amount. When one household pays the other's share, that amount moves from supplier remaining to reimbursement.</p>
        </details>
      </section>

      {summary.nextPayment ? (
        <Link className="money-next" to={`/money/${summary.nextPayment.bookingId}${view === "all" ? "" : `?view=${view}`}`}>
          <p className="kicker">Next expected payment</p>
          <p className="amount-pair tabular">{formatMinor(summary.nextPayment.currency, summary.nextPayment.amountMinor)}</p>
          <p>
            {summary.nextPayment.householdLabel ?? viewer}
            {" · "}
            {summary.nextPayment.date ? formatDateLabel(summary.nextPayment.date) : "Date to confirm"}
            {" · "}
            {dateCertaintyLabel(summary.nextPayment.group)}
          </p>
          <p className="muted">{summary.nextPayment.title}</p>
        </Link>
      ) : (
        <section className="money-next quiet">
          <p className="kicker">Next expected payment</p>
          <p>No dated supplier payment is waiting in this view.</p>
        </section>
      )}

      <section className="repay-panel">
        <h2 className="section-title">
          {view === "all"
            ? "Household reimbursements"
            : summary.reimbursementOwed.length
              ? `To repay ${summary.reimbursementToLabel}`
              : `Nothing to repay ${summary.reimbursementToLabel ?? counterpart ?? "the other household"}`}
        </h2>
        {summary.reimbursementLines.length ? (
          summary.reimbursementLines.map((line) => (
            <div key={`${line.fromHouseholdId}-${line.toHouseholdId}-${line.currency}`}>
              <p className="amount-pair tabular">
                {line.directionLabel} · {formatMinor(line.currency, line.amountMinor)}
              </p>
              {line.bookings.length ? (
                <ul className="plain-list">
                  {line.bookings.map((row) => (
                    <li key={row.bookingId}>
                      <Link to={`/money/${row.bookingId}${view === "all" ? "" : `?view=${view}`}`}>
                        {row.title} · {formatMinor(line.currency, row.amountMinor)}
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ))
        ) : (
          <p>No reimbursement is open in this view.</p>
        )}
        {summary.creditLines.length ? (
          summary.creditLines.map((line) => (
            <p key={`credit-${line.fromHouseholdId}-${line.currency}`}>
              To receive: {line.directionLabel} · {formatMinor(line.currency, line.amountMinor)}.
            </p>
          ))
        ) : null}
        {summary.creditDue.length ? (
          <p>
            Credit: {amountsToSentence(summary.creditDue)}
            {summary.creditFromLabel ? ` · ${summary.creditFromLabel}` : ""}.
            It stays visible until it is applied to a later claim.
          </p>
        ) : null}
        {ledger.claims.length === 0 && ledger.unallocatedCredits.length === 0 ? (
          <p className="muted">An allocation is not a debt. A claim appears only after one household funds the other.</p>
        ) : null}
      </section>

      <section>
        <h2 className="section-title">Payment schedule</h2>
        {schedule.dated.length === 0 && schedule.payAtProperty.length === 0 && schedule.unscheduled.length === 0 ? (
          <p className="muted">Nothing unpaid in this view.</p>
        ) : null}
        {schedule.dated.map((item) => (
          <Link
            key={`${item.bookingId}-${item.householdId}`}
            className={`pay-row ${item.group === "tentative_date" ? "tentative" : ""}`}
            to={`/money/${item.bookingId}${view === "all" ? "" : `?view=${view}`}`}
          >
            <p className="muted">
              {item.date ? formatDateLabel(item.date) : "Date to confirm"}
              <span className="certainty-badge">{dateCertaintyLabel(item.group)}</span>
            </p>
            <p>{item.title}{item.householdLabel ? ` · ${item.householdLabel}` : ""}</p>
            <p className="amount tabular">{formatMinor(item.currency, item.amountMinor)}</p>
          </Link>
        ))}
        {schedule.payAtProperty.length ? <h3 className="section-title">Pay at the property</h3> : null}
        {schedule.payAtProperty.map((item) => (
          <Link key={`${item.bookingId}-${item.householdId}-prop`} className="pay-row" to={`/money/${item.bookingId}${view === "all" ? "" : `?view=${view}`}`}>
            <p>{item.title}{item.householdLabel ? ` · ${item.householdLabel}` : ""}</p>
            <p className="amount tabular">{formatMinor(item.currency, item.amountMinor)}</p>
          </Link>
        ))}
        {schedule.unscheduled.length ? <h3 className="section-title">Date to confirm</h3> : null}
        {schedule.unscheduled.map((item) => (
          <Link key={`${item.bookingId}-${item.householdId}-u`} className="pay-row" to={`/money/${item.bookingId}${view === "all" ? "" : `?view=${view}`}`}>
            <p>{item.title}{item.householdLabel ? ` · ${item.householdLabel}` : ""}</p>
            <p className="amount tabular">{formatMinor(item.currency, item.amountMinor)}</p>
          </Link>
        ))}
      </section>

      <section>
        <h2 className="section-title">Bookings</h2>
        <div className="chip-row" role="tablist" aria-label="Booking payment filter">
          {(["unpaid", "paid", "all"] as const).map((id) => (
            <button key={id} className={`chip ${listFilter === id ? "selected" : ""}`} type="button" role="tab" aria-selected={listFilter === id} onClick={() => setListFilter(id)}>
              {id === "unpaid" ? "Unpaid" : id === "paid" ? "Paid" : "All"}
            </button>
          ))}
        </div>
        {rows.map((row) => (
          <Link key={row.bookingId} className="pay-row" to={`/money/${row.bookingId}${view === "all" ? "" : `?view=${view}`}`}>
            <p>{row.title}{row.householdLabel ? ` · ${row.householdLabel}` : ""}{row.cancelled ? " · Cancelled" : ""}</p>
            {row.missingCost ? <p className="muted">Cost unknown, not treated as zero.</p> : (
              <p className="amount tabular">
                {row.paid && row.originalMinor != null && row.currency
                  ? `Paid · originally ${formatMinor(row.currency, row.originalMinor)}`
                  : row.currency && row.remainingMinor != null
                    ? formatMinor(row.currency, row.remainingMinor)
                    : ""}
              </p>
            )}
          </Link>
        ))}
      </section>

      {role === "owner" ? (
        <div className="row-actions">
          <button className="btn" type="button" onClick={() => setSheet("pay")}>Record payment</button>
          <button className="btn-secondary" type="button" onClick={() => setSheet("settle")}>Record reimbursement</button>
        </div>
      ) : null}

      {sheet === "pay" ? (
        <>
          <button className="sheet-backdrop" type="button" aria-label="Close" onClick={() => setSheet(null)} />
          <div className="sheet" role="dialog" aria-labelledby="pay-sheet-title">
            <div className="menu-sheet-head">
              <h2 id="pay-sheet-title">Record payment</h2>
              <button className="btn-ghost" type="button" onClick={() => setSheet(null)}>Close</button>
            </div>
            <PaymentForm
              initialBookingId={params.get("pay") ?? summary.nextPayment?.bookingId}
              initialPayer={view === "all" ? undefined : view}
              onSaved={() => { setSheet(null); void refresh(); }}
            />
          </div>
        </>
      ) : null}
      {sheet === "settle" ? (
        <>
          <button className="sheet-backdrop" type="button" aria-label="Close" onClick={() => setSheet(null)} />
          <div className="sheet" role="dialog" aria-labelledby="settle-sheet-title">
            <div className="menu-sheet-head">
              <h2 id="settle-sheet-title">Record reimbursement</h2>
              <button className="btn-ghost" type="button" onClick={() => setSheet(null)}>Close</button>
            </div>
            <SettlementForm
              initialFrom={view === "chris" ? "chris" : "paul"}
              initialTo={view === "chris" ? "paul" : "chris"}
              onSaved={() => { setSheet(null); void refresh(); }}
            />
          </div>
        </>
      ) : null}

      <details className="money-tools">
        <summary>Trip totals, converter and print</summary>
        <p className="muted">Known booking costs only. Flights, boats and other missing costs are not zero.</p>
        <p>Known totals {Object.entries(totals.byCurrency).map(([ccy, n]) => formatMinor(ccy, n)).join(" and ")}. This is not a complete holiday total.</p>
        {view !== "all" ? (
          <p className="muted">Allocated shares for the whole trip, every household: {trip.households.map((h) => `${h.label} ${Object.entries(ledger.allocatedByHousehold[h.id] ?? {}).map(([ccy, n]) => formatMinor(ccy, n)).join(" and ") || "none"}`).join(". ")}</p>
        ) : (
          trip.households.map((h) => (
            <p key={h.id}>{h.label} allocated share: {Object.entries(ledger.allocatedByHousehold[h.id] ?? {}).map(([ccy, n]) => formatMinor(ccy, n)).join(" and ") || "none"}</p>
          ))
        )}
        <p className="muted">
          {rate ? `Indicative only: 1 GBP ≈ ${rate.thbPerGbp} THB on ${rate.asOf}. Actual GBP card charges stay on the payment that recorded them.` : "Indicative FX unavailable right now."}
        </p>
        <p><Link to="/essentials/money">Open the pocket converter</Link></p>
        <button className="btn-ghost" type="button" onClick={() => window.print()}>Print this summary</button>
      </details>
    </main>
  );
}
